/* eslint-disable react-hooks/exhaustive-deps */
import dayjs, { Dayjs } from 'dayjs';
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { EvaluationType, Status, StatusCode } from '../../api/enumerations';
import {
  createInspection,
  getAllInspections,
  updateInspections,
} from '../../api/inspections';
import {
  InspectionAgendaData,
  ScheduleData,
} from '../../api/inspections/types';
import { getAllUsers } from '../../api/users';
import { UserData } from '../../api/users/types';
import { editWorkOrder, GetWorkOrder } from '../../api/workOrders';
import { WorkOrderData } from '../../api/workOrders/types';
import { parcelGroup } from '../../constants/selectOptions';
import { GlobalContext } from '../../context/global';
import {
  cleanValue,
  formatPhone,
  formatTime,
  isPlaceholderSelectOption,
  validateCellphone,
  validateFiles,
  validatePhone,
  validateUserName,
} from '../../helpers';
import { useAttachment } from '../../hooks/useAttachmentFiles';
import useErrorMessage from '../../hooks/useErrorMessage';
import useGeneral from '../../hooks/useGeneral';
import useSearchCep from '../../hooks/useSearchCep';

type LatLngLiteral = google.maps.LatLngLiteral;

interface UseScheduleHook {
  propertyData: WorkOrderData | undefined;
  engineer: number;
  setEngineer: (value: number) => void;
  usersData: UserData[] | undefined;
  responsibleName: string;
  setResponsibleName: (value: string) => void;
  contact: string;
  setContact: (value: string) => void;
  date: Dayjs | null;
  setDate: (value: Dayjs | null) => void;
  startTime: Dayjs;
  endTime: Dayjs;
  hasSchedule: boolean;
  scheduledData: InspectionAgendaData[] | undefined;
  address: string;
  setAddress: (value: string) => void;
  addressNumber: string;
  setAddressNumber: (value: string) => void;
  isWithoutNumber: boolean;
  setIsWithoutNumber: (value: boolean) => void;
  age: number;
  setAge: (value: number) => void;
  buildingStandard: number;
  setBuildingStandard: (value: number) => void;
  builtArea: number;
  setBuiltArea: (value: number) => void;
  cep: string;
  city: string;
  setCity: (value: string) => void;
  client: string;
  setClient: (value: string) => void;
  complement: string;
  setComplement: (value: string) => void;
  concept: number;
  setConcept: (value: number) => void;
  conservation: number;
  setConservation: (value: number) => void;
  district: string;
  setDistrict: (value: string) => void;
  goal: number;
  setGoal: (value: number) => void;
  judicialDistrict: string;
  setJudicialDistrict: (value: string) => void;
  parkingLots: number;
  setParkingLots: (value: number) => void;
  propertyFunction: number;
  setPropertyFunction: (value: number) => void;
  propertyUse: number;
  setPropertyUse: (value: number) => void;
  registerNumber: string;
  setRegisterNumber: (value: string) => void;
  registryOffice: string;
  setRegistryOffice: (value: string) => void;
  book: string;
  setBook: (value: string) => void;
  registrationUf: number;
  setRegistrationUf: (value: number) => void;
  propertyRooms: number;
  setPropertyRooms: (value: number) => void;
  solicitor: string;
  setSolicitor: (value: string) => void;
  suites: number;
  setSuites: (value: number) => void;
  toilets: number;
  setToilets: (value: number) => void;
  totalArea: number;
  setTotalArea: (value: number) => void;
  uf: string;
  setUf: (value: string) => void;
  zone: number;
  setZone: (value: number) => void;
  registerFileName: string;
  iptuFileName: string;
  searchMap: boolean;
  setSearchMap: (value: boolean) => void;
  isAddressEdited: boolean;
  setIsAddressEdited: (value: boolean) => void;
  hasSearched: boolean;
  setHasSearched: (value: boolean) => void;
  pinPlace: LatLngLiteral;
  setPinPlace: (value: LatLngLiteral) => void;
  enableEditing: boolean;
  setEnableEditing: (value: boolean) => void;
  today: string[];
  getEngineerEvents: (id: number, date: string) => Promise<void>;
  handleStartTime: (value: Dayjs | null) => void;
  handleEndTime: (value: Dayjs | null) => void;
  handleSchedule: (edit: boolean) => Promise<void>;
  handleScheduleExist: () => boolean;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  verifyCep: (cep: string) => Promise<void>;
  handleFileUpload: (
    e: ChangeEvent<HTMLInputElement>,
    value: string
  ) => Promise<void>;
  loadingPage: boolean;
  downloadIptu: string;
  downloadRegister: string;
  completeScheduling: boolean;
}

export default function useSchedule(): UseScheduleHook {
  const [loadingPage, setLoadingPage] = useState(true);
  const [propertyData, setPropertyData] = useState<WorkOrderData>();
  const [usersData, setUsersData] = useState<UserData[]>([]);
  const [engineer, setEngineer] = useState(0);
  const [responsibleName, setResponsibleName] = useState('');
  const [contact, setContact] = useState('');
  const [date, setDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<Dayjs>(dayjs());
  const [endTime, setEndTime] = useState<Dayjs>(dayjs());
  const [formattedStartHour, setFormattedStartHour] = useState('');
  const [formattedEndHour, setFormattedEndHour] = useState('');
  const [scheduledData, setScheduledData] = useState<InspectionAgendaData[]>();
  const [hasSchedule, setHasSchedule] = useState(false);
  const [scheduleId, setScheduleId] = useState(0);
  const [enableEditing, setEnableEditing] = useState(false);
  const [page, setPage] = useState(1);
  const [lastCalledPage, setLastCalledPage] = useState(0);
  const [downloadIptu, setDownloadIptu] = useState('');
  const [downloadRegister, setDownloadRegister] = useState('');
  const [isAddressEdited, setIsAddressEdited] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Client data
  const [client, setClient] = useState('');
  const [solicitor, setSolicitor] = useState('');
  const [goal, setGoal] = useState(0);
  const [propertyFunction, setPropertyFunction] = useState(0);
  const [propertyUse, setPropertyUse] = useState(0);

  // Property data
  const [registerFileName, setRegisterFileName] = useState('');
  const [registerFile, setRegisterFile] = useState<File | string>();
  const [iptuFileName, setIptuFileName] = useState('');
  const [iptuFile, setIptuFile] = useState<File | string>();
  const [registerNumber, setRegisterNumber] = useState('');
  const [registryOffice, setRegistryOffice] = useState('');
  const [book, setBook] = useState('');
  const [concept, setConcept] = useState(0);
  const [zone, setZone] = useState(0);
  const [judicialDistrict, setJudicialDistrict] = useState('');
  const [registrationUf, setRegistrationUf] = useState(0);

  // Property address
  const [address, setAddress] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [isWithoutNumber, setIsWithoutNumber] = useState(false);
  const [complement, setComplement] = useState('');
  const [cep, setCep] = useState('');
  const [district, setDistrict] = useState('');
  const [city, setCity] = useState('');
  const [uf, setUf] = useState('');
  const [searchMap, setSearchMap] = useState(true);
  const [pinPlace, setPinPlace] = useState<LatLngLiteral>({
    lat: -23.56162,
    lng: -46.65591,
  });

  // Property informations
  const [totalArea, setTotalArea] = useState(0);
  const [builtArea, setBuiltArea] = useState(0);
  const [propertyRooms, setPropertyRooms] = useState(0);
  const [toilets, setToilets] = useState(0);
  const [suites, setSuites] = useState(0);
  const [parkingLots, setParkingLots] = useState(0);
  const [age, setAge] = useState(0);
  const [buildingStandard, setBuildingStandard] = useState(0);
  const [conservation, setConservation] = useState(0);
  const [completeScheduling, setCompleteScheduling] = useState(false);

  const today = new Date().toISOString().split('T');
  const isParcelGroup =
    propertyData && parcelGroup.has(propertyData.real_estate_kind);

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage, isHiveOwner } =
    useContext(GlobalContext);

  const { iptuLink, setIptuLink, registerLink, setRegisterLink } =
    useAttachment();
  const { getErrorMessage } = useErrorMessage();
  const { osId, navigateHome } = useGeneral();
  const { handleSearchCep } = useSearchCep();

  const getEngineerEvents = async (
    engId: number,
    date: string
  ): Promise<void> => {
    const newDate = dayjs(date);
    const startDate = newDate.startOf('week').format('YYYY-MM-DD');
    const endDate = newDate.endOf('week').format('YYYY-MM-DD');
    const url = `&users_ids=${engId}`;

    try {
      const response = await getAllInspections(startDate, endDate, url);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setScheduledData(response.data);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  const getDataCallback = useCallback(async () => {
    try {
      const response = await GetWorkOrder(osId);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (!response.data || response.data.status !== Status.SCHEDULE) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setPropertyData(response.data);
      setLoadingPage(false);

      if (response.data.inspection) {
        setHasSchedule(true);
        if (!isHiveOwner) {
          setScheduleId(response.data.inspection.id);
          setResponsibleName(response.data.inspection.responsible);
          setContact(formatPhone(response.data.inspection.responsible_phone));
          setEngineer(response.data.inspection.engineer_id);
          setDate(dayjs(response.data.inspection.schedule_at));
          setFormattedStartHour(response.data.inspection.start_at);
          setFormattedEndHour(response.data.inspection.finish_at);
          setStartTime(
            dayjs(
              `${response.data.inspection.schedule_at}T${response.data.inspection.start_at}`
            )
          );
          setEndTime(
            dayjs(
              `${response.data.inspection.schedule_at}T${response.data.inspection.finish_at}`
            )
          );
          getEngineerEvents(
            response.data.inspection.engineer_id,
            response.data.inspection.schedule_at
          );
        }
      }

      setClient(response.data.client_name);
      setSolicitor(response.data.requester_name);
      setGoal(response.data.report_goal);
      setPropertyFunction(response.data.report_function);
      setPropertyUse(response.data.use);

      setDownloadRegister(response.data.real_estate_registry || '');
      setDownloadIptu(response.data.iptu_document || '');
      setRegisterNumber(response.data.registration_number);
      setRegistryOffice(response.data.registry_office ?? '');
      setBook(response.data.book ?? '');
      setConcept(response.data.communities_kind);
      setZone(response.data.zone);
      setJudicialDistrict(response.data.judicial_district);
      setRegistrationUf(response.data.uf);

      setAddress(response.data.street);
      setComplement(response.data.address_complement);
      setCep(response.data.zip_code);
      setDistrict(response.data.district);
      setCity(response.data.city);
      setUf(response.data.state);
      if (response.data.latitude && response.data.longitude) {
        setPinPlace({
          lat: Number(response.data.latitude),
          lng: Number(response.data.longitude),
        });
      }

      setTotalArea(response.data.total_area || 0);
      setBuiltArea(response.data.constructed_area || 0);
      setPropertyRooms(response.data.bedrooms || 0);
      setToilets(response.data.bathrooms || 0);
      setSuites(response.data.suites || 0);
      setParkingLots(response.data.parking_spaces || 0);
      setAge(response.data.age || 0);
      setBuildingStandard(response.data.constructive_standard || 0);
      setConservation(response.data.preservation_state || 0);
      if (response.data.number) setAddressNumber(response.data.number);
      else setIsWithoutNumber(true);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error, true));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  }, [iptuLink, registerLink]);

  const getEngineers = useCallback(async () => {
    if (page === lastCalledPage) {
      return;
    }

    try {
      const response = await getAllUsers(page, 10);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (!response.data) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      const activeEngineers = response.data.filter(
        (engineer) => engineer.is_active
      );

      setUsersData([...usersData, ...activeEngineers]);
      setLastCalledPage(page);

      if (response.detail.total_pages && response.detail.total_pages > page) {
        setPage(page + 1);
      }
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  }, [page]);

  useEffect(() => {
    getDataCallback();
  }, [iptuLink, registerLink]);

  useEffect(() => {
    if (!isHiveOwner) {
      getEngineers();
    }
  }, [propertyData, page]);

  const handleSchedule = async (editSchedule: boolean): Promise<void> => {
    if (engineer === 0) {
      setSnackbarMessage('Selecione um vistoriador.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    if (!validateUserName(responsibleName)) {
      setSnackbarMessage('O nome do responsável é obrigatório.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    if (!date || !formattedStartHour || !formattedEndHour) {
      setSnackbarMessage('Os campos de data e hora são obrigatórios');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    const phone = cleanValue(contact);

    if (
      phone.length < 10 ||
      (phone.length === 10 && !validatePhone(phone)) ||
      (phone.length === 11 && !validateCellphone(phone))
    ) {
      setSnackbarMessage('Contato inválido, adicione um número válido.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    getEngineerEvents(engineer, dayjs(date).format('YYYY-MM-DD'));

    const scheduleData: ScheduleData = {
      schedule_at: dayjs(date).format('YYYY-MM-DD'),
      start_at: formattedStartHour,
      finish_at: formattedEndHour,
      responsible: responsibleName,
      responsible_phone: phone,
      engineer_id: engineer,
      work_order_id: osId,
    };

    if (propertyData?.inspection?.form_response) {
      scheduleData.form_response = JSON.stringify(
        propertyData?.inspection?.form_response
      );
    }

    if (editSchedule) {
      try {
        const response = await updateInspections(scheduleId, scheduleData);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (response.detail.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        setSnackbarMessage('Agendamento alterado com sucesso!');
        setErrorMessage(false);
        setOpenSnackbar(true);
        getEngineerEvents(engineer, scheduleData.schedule_at);
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
      }
    } else {
      try {
        const response = await createInspection(scheduleData);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (response.detail.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        setSnackbarMessage('Agendamento realizado com sucesso!');
        setErrorMessage(false);
        setOpenSnackbar(true);
        getEngineerEvents(engineer, scheduleData.schedule_at);
        setHasSchedule(true);
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
      }
    }
  };

  const handleStartTime = (newValue: Dayjs | null): void => {
    if (newValue) {
      const endTime = dayjs(newValue).add(1, 'hours');
      setStartTime(newValue);
      setEndTime(endTime);
      setFormattedStartHour(formatTime(newValue));
      setFormattedEndHour(formatTime(endTime));
    }
  };

  const handleEndTime = (newValue: Dayjs | null): void => {
    if (newValue) {
      setEndTime(newValue);
      setFormattedEndHour(formatTime(newValue));
    }
  };

  const handleScheduleExist = (): boolean => {
    if (
      !hasSchedule &&
      (propertyData?.evaluation_type === EvaluationType.SIMPFACTORS ||
        propertyData?.evaluation_type === EvaluationType.SIMPINFERENCES)
    ) {
      setSnackbarMessage(
        'É necessário adicionar um agendamento para aprovar esta OS.'
      );
      setErrorMessage(true);
      setOpenSnackbar(true);
      return false;
    }
    return true;
  };

  const verifyCep = async (cep: string): Promise<void> => {
    const addressData = await handleSearchCep(cep);
    if (addressData) {
      setCep(addressData.cep);
      setUf(addressData.uf);
      setCity(addressData.city);
      setDistrict(addressData.district);
      setAddress(addressData.address);
    }
    setIsAddressEdited(true);
  };

  const handleFileUpload = async (
    e: ChangeEvent<HTMLInputElement>,
    fileType: string
  ): Promise<void> => {
    if (!e.target.files?.item(0) || !validateFiles(e.target.files[0].type)) {
      setSnackbarMessage('Formato incorreto, selecione uma imagem ou pdf.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    const file = e.target.files[0];

    const formData = new FormData();
    if (fileType === 'register') {
      formData.append('real_estate_registry', file);
    }
    if (fileType === 'iptu') {
      formData.append('iptu_document', file);
    }

    try {
      const response = await editWorkOrder(osId, formData);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setSnackbarMessage('Dados alterados com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      if (fileType === 'register') {
        setRegisterLink(file.name);
        setRegisterFileName(file.name);
        setRegisterFile(file);
      }
      if (fileType === 'iptu') {
        setIptuLink(file.name);
        setIptuFileName(file.name);
        setIptuFile(file);
      }
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!cep) {
      setSnackbarMessage('Adicione a localização do imóvel');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    if (isAddressEdited) {
      setSnackbarMessage('Pesquise a localização no mapa antes de salvar.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    const requiredSelectFields = [
      concept,
      goal,
      propertyFunction,
      propertyUse,
      registrationUf,
      ...(!isParcelGroup ? [buildingStandard, conservation] : []),
    ];
    if (requiredSelectFields.some(isPlaceholderSelectOption)) {
      setSnackbarMessage('Os dados de seleção são obrigatórios.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    const formData = new FormData();
    formData.append('street', address);
    formData.append('zip_code', cep);
    formData.append('city', city);
    formData.append('client_name', client);
    formData.append('address_complement', complement);
    formData.append('communities_kind', concept.toString());
    formData.append('district', district);
    formData.append('report_goal', goal.toString());
    formData.append('judicial_district', judicialDistrict);
    formData.append('report_function', propertyFunction.toString());
    formData.append('use', propertyUse.toString());
    formData.append('registration_number', registerNumber);
    formData.append('registry_office', registryOffice);
    formData.append('book', book);
    formData.append('requester_name', solicitor);
    formData.append('uf', registrationUf.toString());
    formData.append('total_area', totalArea.toString());
    formData.append('state', uf);
    formData.append('zone', zone.toString());
    formData.append('latitude', pinPlace.lat.toString());
    formData.append('longitude', pinPlace.lng.toString());

    formData.append('number', isWithoutNumber ? 'null' : addressNumber);

    if (!isParcelGroup) {
      formData.append('constructed_area', builtArea.toString());
      formData.append('bedrooms', propertyRooms.toString());
      formData.append('bathrooms', toilets.toString());
      formData.append('suites', suites.toString());
      formData.append('parking_spaces', parkingLots.toString());
      formData.append('age', age.toString());
      formData.append('constructive_standard', buildingStandard.toString());
      formData.append('preservation_state', conservation.toString());
    }

    if (registerFile) {
      formData.append('real_estate_registry', registerFile);
    }
    if (iptuFile) {
      formData.append('iptu_document', iptuFile);
    }

    try {
      const response = await editWorkOrder(osId, formData);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setSnackbarMessage('Dados alterados com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      navigateHome();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  useEffect(() => {
    if (hasSchedule) {
      setCompleteScheduling(true);
    } else {
      setCompleteScheduling(false);
    }
  }, [hasSchedule]);

  return {
    propertyData,
    engineer,
    setEngineer,
    usersData,
    responsibleName,
    setResponsibleName,
    contact,
    setContact,
    date,
    setDate,
    startTime,
    endTime,
    hasSchedule,
    scheduledData,
    address,
    setAddress,
    addressNumber,
    setAddressNumber,
    isWithoutNumber,
    setIsWithoutNumber,
    age,
    setAge,
    buildingStandard,
    setBuildingStandard,
    builtArea,
    setBuiltArea,
    cep,
    city,
    setCity,
    client,
    setClient,
    complement,
    setComplement,
    concept,
    setConcept,
    conservation,
    setConservation,
    district,
    setDistrict,
    goal,
    setGoal,
    judicialDistrict,
    setJudicialDistrict,
    parkingLots,
    setParkingLots,
    propertyFunction,
    setPropertyFunction,
    propertyUse,
    setPropertyUse,
    registerNumber,
    setRegisterNumber,
    registryOffice,
    setRegistryOffice,
    book,
    setBook,
    registrationUf,
    setRegistrationUf,
    propertyRooms,
    setPropertyRooms,
    solicitor,
    setSolicitor,
    suites,
    setSuites,
    toilets,
    setToilets,
    totalArea,
    setTotalArea,
    uf,
    setUf,
    zone,
    setZone,
    registerFileName,
    iptuFileName,
    searchMap,
    setSearchMap,
    isAddressEdited,
    setIsAddressEdited,
    hasSearched,
    setHasSearched,
    pinPlace,
    setPinPlace,
    enableEditing,
    setEnableEditing,
    today,
    getEngineerEvents,
    handleStartTime,
    handleEndTime,
    handleSchedule,
    handleScheduleExist,
    handleSubmit,
    verifyCep,
    handleFileUpload,
    loadingPage,
    downloadIptu,
    downloadRegister,
    completeScheduling,
  };
}
